import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useStructuraAuth } from '@structura/auth'
import { useTranslation, TranslationKey } from '@structura/i18n'
import { beamRouteForMaterial } from '@structura/services'
import { BEAM_TEMPLATE_STORAGE_KEY } from '@structura/beam-analysis'
import '@structura/ui/Dashboard.css'

interface BeamTemplate {
  id: string
  nameKey: TranslationKey
  descriptionKey: TranslationKey
  icon: string
  material: 'concrete' | 'steel' | 'timber'
  timberKind?: 'glulam' | 'solid'
  materialLabel: string
  spans: number
  spanLengths: number[]
  loadKey: TranslationKey
  context: string
  accent: string
}

const concreteTemplates: BeamTemplate[] = [
  {
    id: 'concrete-residential',
    nameKey: 'beam.dashboard.tpl.concrete-residential.name',
    descriptionKey: 'beam.dashboard.tpl.concrete-residential.description',
    icon: 'bi-house-door',
    material: 'concrete',
    materialLabel: 'C30/37',
    spans: 1,
    spanLengths: [6],
    loadKey: 'beam.dashboard.tpl.concrete-residential.load',
    context: 'residential',
    accent: 'steelblue'
  },
  {
    id: 'concrete-office',
    nameKey: 'beam.dashboard.tpl.concrete-office.name',
    descriptionKey: 'beam.dashboard.tpl.concrete-office.description',
    icon: 'bi-building',
    material: 'concrete',
    materialLabel: 'C35/45',
    spans: 2,
    spanLengths: [8, 8],
    loadKey: 'beam.dashboard.tpl.concrete-office.load',
    context: 'commercial',
    accent: 'steelblue'
  },
  {
    id: 'concrete-parking',
    nameKey: 'beam.dashboard.tpl.concrete-parking.name',
    descriptionKey: 'beam.dashboard.tpl.concrete-parking.description',
    icon: 'bi-car-front',
    material: 'concrete',
    materialLabel: 'C30/37',
    spans: 3,
    spanLengths: [7.5, 7.5, 7.5],
    loadKey: 'beam.dashboard.tpl.concrete-parking.load',
    context: 'parking',
    accent: 'steelblue'
  },
  {
    id: 'concrete-industrial',
    nameKey: 'beam.dashboard.tpl.concrete-industrial.name',
    descriptionKey: 'beam.dashboard.tpl.concrete-industrial.description',
    icon: 'bi-gear-wide-connected',
    material: 'concrete',
    materialLabel: 'C40/50',
    spans: 2,
    spanLengths: [10, 10],
    loadKey: 'beam.dashboard.tpl.concrete-industrial.load',
    context: 'industrial',
    accent: 'steelblue'
  },
  {
    id: 'concrete-transfer',
    nameKey: 'beam.dashboard.tpl.concrete-transfer.name',
    descriptionKey: 'beam.dashboard.tpl.concrete-transfer.description',
    icon: 'bi-arrows-collapse',
    material: 'concrete',
    materialLabel: 'C45/55',
    spans: 1,
    spanLengths: [8],
    loadKey: 'beam.dashboard.tpl.concrete-transfer.load',
    context: 'commercial',
    accent: 'steelblue'
  }
]

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const authState = useStructuraAuth()
  const { t } = useTranslation()

  const handleTemplateSelect = (template: BeamTemplate): void => {
    const templateData = {
      templateId: template.id,
      material: template.material,
      timberKind: template.timberKind,
      spanLengths: template.spanLengths,
      context: template.context
    }
    sessionStorage.setItem(BEAM_TEMPLATE_STORAGE_KEY, JSON.stringify(templateData))
    navigate(beamRouteForMaterial(template.material))
  }

  const handleStartBlank = (): void => {
    sessionStorage.removeItem(BEAM_TEMPLATE_STORAGE_KEY)
    navigate('/beam/concrete')
  }

  const greeting = React.useMemo((): string => {
    const hour = new Date().getHours()
    if (hour < 12) {
      return t('beam.dashboard.greetingMorning')
    }
    if (hour < 18) {
      return t('beam.dashboard.greetingAfternoon')
    }
    return t('beam.dashboard.greetingEvening')
  }, [t])

  const firstName = authState.userName?.split(' ')[0] || t('beam.dashboard.defaultName')

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero">
        <div className="container">
          <div className="dashboard-welcome">
            <div className="welcome-badge">
              <i className="bi bi-lightning-charge-fill me-2"></i>
              {t('beam.dashboard.readyToDesign')}
            </div>
            <h1 className="welcome-title">{t('beam.dashboard.welcomeTitle', { greeting, name: firstName })}</h1>
            <p className="welcome-subtitle">
              {t('beam.dashboard.welcomeSubtitle')}
            </p>
          </div>

          <div className="quick-actions">
            <button className="quick-action-btn primary" onClick={handleStartBlank}>
              <div className="action-icon">
                <i className="bi bi-plus-lg"></i>
              </div>
              <div className="action-content">
                <span className="action-title">{t('beam.dashboard.newBlankDesign')}</span>
                <span className="action-desc">{t('beam.dashboard.newBlankDesc')}</span>
              </div>
              <i className="bi bi-arrow-right action-arrow"></i>
            </button>
            <button className="quick-action-btn secondary" onClick={() => navigate('/account')}>
              <div className="action-icon">
                <i className="bi bi-person-badge"></i>
              </div>
              <div className="action-content">
                <span className="action-title">{t('beam.dashboard.myAccount')}</span>
                <span className="action-desc">{t('beam.dashboard.myAccountDesc')}</span>
              </div>
              <i className="bi bi-arrow-right action-arrow"></i>
            </button>
          </div>
        </div>
      </div>

      <section className="templates-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-grid-3x3-gap me-2"></i>
              {t('beam.dashboard.beamTemplates')}
            </h2>
            <p className="section-desc">
              {t('beam.dashboard.beamTemplatesDesc')}
            </p>
          </div>

          {/* Single-material app — render the templates as a responsive tile
              grid, not the legacy 3-column-per-material layout. */}
          <div className="column-header column-concrete templates-section-header">
            <i className="bi bi-bricks me-2"></i>
            {t('beam.dashboard.materialConcrete')}
          </div>
          <div className="templates-grid">
            {concreteTemplates.map((template: BeamTemplate) => (
              <TemplateCard key={template.id} template={template} onSelect={handleTemplateSelect} />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits section — same canonical content as the anonymous Home page,
          rendered here too so logged-in users see it on /dashboard without
          having to bounce back to /. The previous, duplicated features-grid
          (Advanced Analysis / Visual Results / Professional Reports / Code
          Checking) was retired in favour of this single source of truth. */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('beam.dashboard.whyChoose')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitMultiSpanLabel')}</strong> {t('beam.dashboard.benefitMultiSpanDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitEurocodeLabel')}</strong> {t('beam.dashboard.benefitEurocodeDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitVisualizationLabel')}</strong> {t('beam.dashboard.benefitVisualizationDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitResultsLabel')}</strong> {t('beam.dashboard.benefitResultsDesc')}
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('beam.dashboard.technicalFeatures')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-gear-fill home-icon me-2"></i>
                  {t('beam.dashboard.featureLoadTypes')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-graph-up home-icon me-2"></i>
                  {t('beam.dashboard.featureDiagrams')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-diagram-2-fill home-icon me-2"></i>
                  {t('beam.dashboard.featureReactions')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-file-earmark-text home-icon me-2"></i>
                  {t('beam.dashboard.featureLoadCases')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

interface TemplateCardProps {
  template: BeamTemplate
  onSelect: (template: BeamTemplate) => void
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onSelect }) => {
  const { t } = useTranslation()
  const spansSpec = t(template.spans > 1 ? 'beam.dashboard.spansPlural' : 'beam.dashboard.spansSingular', { count: template.spans, lengths: template.spanLengths.join(' + ') })
  return (
    <article
      className="template-card"
      style={{ '--template-accent': template.accent } as React.CSSProperties}
      onClick={() => onSelect(template)}
    >
      <div className="template-header">
        <div className="template-icon">
          <i className={`bi ${template.icon}`}></i>
        </div>
        <span className={`material-badge material-${template.material}`}>
          {template.materialLabel}
          {template.timberKind === 'solid' && t('beam.dashboard.solidSuffix')}
          {template.timberKind === 'glulam' && t('beam.dashboard.glulamSuffix')}
        </span>
      </div>
      <h3 className="template-title">{t(template.nameKey)}</h3>
      <p className="template-description">{t(template.descriptionKey)}</p>
      <div className="template-specs">
        <div className="spec-item">
          <i className="bi bi-arrows-expand"></i>
          <span>{spansSpec}</span>
        </div>
        <div className="spec-item">
          <i className="bi bi-arrow-down-circle"></i>
          <span>{t(template.loadKey)}</span>
        </div>
      </div>
      <div className="template-footer">
        <span className="use-template">
          {t('beam.dashboard.useTemplate')}
          <i className="bi bi-arrow-right ms-2"></i>
        </span>
      </div>
    </article>
  )
}

export default Dashboard
